import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import NativeSelect from "@meetyoo/components/src/components/Selects/NativeSelect";

import { moment } from "../../../utils/LocalizedUtils";

import { setFilter, clearFilter, reCheckErrors } from "../../../actions/billing";

const customStyle = () => ({
  formControl: { width: "500px" },
});

const objectBillingPoint = {
  EMPTY: "",
  ALLES: "Alle Posten",
  KM: "Kalendermonat",
  KM_VORAUS: "Kalendermonat im Voraus",
  PERIODE: "Abrechnungsperiode",
  QM: "Kalenderquartal",
  VJ: "Vertragsjahr",
  VJ_VORAUS: "Vertragsjahr im Voraus",
  VM: "Vertragsmonat",
  VM_VORAUS: "Vertragsmonat im Voraus",
  VQ_VORAUS: "Vertragsquartal im Voraus",
};

const objectBillingTime = {
  EMPTY: "",
  "1+16 KM": "1. und 16. im Kalendermonat (regelmäßig)",
  "15KM": "15. Tag im Kalendermonat",
  "16+LT KM": "16. und letzter Tag im Kalendermonat (regelmäßig)",
  "ERSTER KM": "erster Tag im Kalendermonat (regelmäßig)",
  "ERSTER VM": "erster Tag im Vertragsmonat (regelmäßig)",
  "LETZTER KM": "letzter Tag im Kalendermonat (regelmäßig)",
  "LETZTER VM": "letzter Tag im Vertragsmonat (regelmäßig)",
  MANUELL: "keine automatische Abrechnung",
  SOFORT: "sofortige Berechnung",
  VJ: "Vertragsjahr",
  VM: "Vertragsmonat",
  VQ: "Vertragsquartal",
  "VT-BEGINN": "Vertragsbeginn (einmalig)",
  "VT-ENDE": "Vertragsende (einmalig)",
};

const filterObjectBillingPoint = selectedValue => {
  const value = Object.keys(objectBillingPoint).filter(key => objectBillingPoint[key] === selectedValue)[0];
  return value !== "EMPTY" ? value : "";
};

const filterObjectBillingTime = selectedValue => {
  const value = Object.keys(objectBillingTime).filter(key => objectBillingTime[key] === selectedValue)[0];
  return value !== "EMPTY" ? value : "";
};

export const BillingLedgerEntryFilterComponent = ({
  getLedgerEntriesHandler,
  setResetPage,
  setMadeRequest,
  requestBillingInProgress,
  translate,
  filter,
  setFilterFn,
  clearFilterFn,
  reCheckErrorsFn,
}) => {
  const theme = useTheme();
  const handleSelectChangeBillingPoint = name => event => {
    setFilterFn({ ...filter, [name]: filterObjectBillingPoint(event.target.value) });
  };

  const handleSelectChangeBillingTime = name => event => {
    setFilterFn({ ...filter, [name]: filterObjectBillingTime(event.target.value) });
  };

  const handleTextChange = name => event => {
    setFilterFn({ ...filter, [name]: event.target.value });
  };

  const handleDateChange = (name, date) => {
    setFilterFn({ ...filter, [name]: moment.utc(date) });
  };

  const textFieldSx = theme => ({
    ml: 1,
    mr: 1,
    flexGrow: 1,
  });

  const tf = (id, labelKey, field) => (
    <TextField
      id={id}
      label={translate[labelKey]}
      sx={textFieldSx}
      value={filter[field] ?? ""}
      onChange={handleTextChange(field)}
    />
  );

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", width: "100%" }}>
      <Typography variant="subtitle1" id="filterTitle">
        Filter
      </Typography>
      <Box sx={{ width: "100%" }} />
      {tf("ledger-entry-conference-id", "conferenceId", "confId")}
      {tf("ledger-entry-stat", "status", "status")}
      <DateTimePicker
        ampm={false}
        format="DD.MM.YYYY HH:mm:ss"
        value={filter.confStart ? moment(filter.confStart) : null}
        onChange={date => handleDateChange("confStart", date)}
        slotProps={{
          textField: {
            id: "ledger-entry-start-time",
            label: translate.confStartTime,
            sx: textFieldSx,
          },
        }}
      />
      <DateTimePicker
        ampm={false}
        format="DD.MM.YYYY HH:mm:ss"
        value={filter.confEnd ? moment(filter.confEnd) : null}
        onChange={date => handleDateChange("confEnd", date)}
        slotProps={{
          textField: {
            id: "ledger-entry-end-time",
            label: translate.confEndTime,
            sx: textFieldSx,
          },
        }}
      />
      {tf("ledger-entry-error", "errorCodeLedgerEntry", "error")}
      {tf("ledger-entry-contract", "contract", "contract")}
      {tf("ledger-entry-product-number", "phoneNumber", "productno")}
      {tf("ledger-entry-name", "name", "name")}
      {tf("ledger-entry-request", "requestFrom", "requestFrom")}
      {tf("ledger-entry-participant", "participantName", "participantName")}
      {tf("ledger-entry-b-phone", "bPhoneNumber", "bPhoneNumber")}
      <Box sx={{ width: "100%" }} />
      <NativeSelect
        customStyle={customStyle}
        id="ledger-entry-billingTime"
        value={translate.billingTime}
        currentSelect={objectBillingTime[filter.billingTime !== "" ? filter.billingTime : "EMPTY"]}
        selectValues={Object.keys(objectBillingTime).map(key => objectBillingTime[key])}
        onChange={handleSelectChangeBillingTime("billingTime")}
      />
      <NativeSelect
        customStyle={customStyle}
        id="ledger-entry-billingPoint"
        value={translate.billingPoint}
        currentSelect={objectBillingPoint[filter.billingPoint !== "" ? filter.billingPoint : "EMPTY"]}
        selectValues={Object.keys(objectBillingPoint).map(key => objectBillingPoint[key])}
        onChange={handleSelectChangeBillingPoint("billingPoint")}
      />
      <Divider />
      <Box sx={{ mt: 1, width: "100%", display: "inline-flex" }}>
        <Box sx={{ flex: 1, width: "10px", ml: "8px", alignContent: theme.left }} />
        <Button variant="contained" size="small" color="primary" onClick={() => reCheckErrorsFn()}>
          Check errors
        </Button>
        <Box sx={{ flex: 1, width: "10px" }} />
        <Button size="small" onClick={() => clearFilterFn()}>
          {translate.clearButtonFilter}
        </Button>
        <Button
          disabled={requestBillingInProgress}
          variant="contained"
          size="small"
          color="primary"
          onClick={() => {
            setResetPage(true);
            setFilterFn({ ...filter, page: 0 });
            setMadeRequest(true);
            getLedgerEntriesHandler({ ...filter, page: 0 });
          }}
        >
          {translate.searchButtonFilter}
        </Button>
      </Box>
    </Box>
  );
};

BillingLedgerEntryFilterComponent.propTypes = {
  getLedgerEntriesHandler: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  translate: PropTypes.object.isRequired,
  setFilterFn: PropTypes.func.isRequired,
  clearFilterFn: PropTypes.func.isRequired,
  setMadeRequest: PropTypes.func.isRequired,
  setResetPage: PropTypes.func.isRequired,
  reCheckErrorsFn: PropTypes.func.isRequired,
  requestBillingInProgress: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  filter: state.billing.filter,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  setFilterFn: filter => {
    dispatch(setFilter(filter));
  },
  clearFilterFn: () => dispatch(clearFilter()),
  reCheckErrorsFn: () => dispatch(reCheckErrors()),
});

const BillingLedgerEntryFilter = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingLedgerEntryFilterComponent);

export default BillingLedgerEntryFilter;
